import * as React from 'react'

import {
  AsyncSelect,
  SubText,
  Text,
  type IAsyncSelectProps,
} from '@procurify/ui'
import {
  components as ReactSelectComponents,
  type OptionProps,
  type SingleValueProps,
} from 'react-select'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { cardRepository } from '../../data-access/repositories/CardRepository'
import { Card } from '../../entities/Card'
import { CardDisplayStatuses } from '../../entities/enums/CardDisplayStatusEnum'
import { useFormatCardStatusLabel } from '../../hooks/useFormatCardStatusLabel/useFormatCardStatusLabel'

const Option = ({
  card,
  showCardStatus = true,
}: {
  card: Card
  showCardStatus?: boolean
}) => {
  const formatCardStatusLabel = useFormatCardStatusLabel()
  const { cardDisplayStatus, last4Digits, cardholder } = card

  const getStatusColor = () => {
    switch (cardDisplayStatus) {
      case CardDisplayStatuses.ACTIVE:
        return 'disabled'
      case CardDisplayStatuses.PENDING:
        return 'warning'
      default:
        return 'disabled'
    }
  }

  return (
    <>
      <Text autoMargin={false}>
        {last4Digits} - {cardholder.fullName}
      </Text>
      {cardDisplayStatus && showCardStatus && (
        <SubText color={getStatusColor()} bold autoMargin={false}>
          {formatCardStatusLabel(cardDisplayStatus)}
        </SubText>
      )}
    </>
  )
}

const SelectedValue = (props: SingleValueProps<Card, false>) => {
  const { data } = props

  return (
    <ReactSelectComponents.SingleValue {...props}>
      <Option card={data} showCardStatus={false} />
    </ReactSelectComponents.SingleValue>
  )
}

const SelectOption = (props: OptionProps<Card, false>) => {
  const { data } = props

  return (
    <ReactSelectComponents.Option {...props}>
      <Option card={data} />
    </ReactSelectComponents.Option>
  )
}

export interface ICardSelectProps extends IAsyncSelectProps<Card> {}

export const RequestableCardSelect = ({ ...props }: ICardSelectProps) => {
  const intl = useProcurifyIntl()

  const loadOptions: IAsyncSelectProps<Card>['loadOptions'] = async (
    inputValue,
    pagination,
    callback
  ) => {
    const res = await cardRepository.getRequestableCards({
      params: { search: inputValue, page: pagination.page },
    })

    const data = res?.data || []

    callback({
      options: data.map((card) => new Card(card)),
      pagination: {
        hasMore: res?.pagination
          ? res.pagination?.page < res.pagination?.numPages
          : false,
        page: pagination.page + 1,
      },
    })
  }

  return (
    <AsyncSelect<Card>
      cacheOptions={true}
      loadOptions={loadOptions}
      placeholder={intl.formatMessage(messages.placeholderText)}
      components={{
        SingleValue: SelectedValue,
        Option: SelectOption,
      }}
      getOptionLabel={(card) =>
        `${card.last4Digits} - ${card.cardholder.fullName}`
      }
      getOptionValue={(option) => (option ? option.id.toString() : '')}
      {...props}
    />
  )
}
